import { findImportTargetField } from "./target-fields.js";
const issue = (code, columnIndex = null, targetField = null) => Object.freeze({ code, columnIndex, targetField });
/**
 * Checks a mapping and lists every problem (an empty list means it is valid). A message is rendered
 * from the code; nothing here echoes header or cell text.
 *
 * A `draft` may keep columns UNMAPPED, so staff can save and come back. A `submit` may not: every
 * column needs an explicit decision (mapped or ignored), the name must be mapped, and the item type
 * must come from a mapped `item_type` column or from the job default (not both).
 */
export function validateMapping(input) {
    const issues = [];
    const seen = new Set();
    const targetClaims = new Map();
    let nameMapped = false;
    let itemTypeMapped = false;
    for (const decision of input.decisions) {
        const { columnIndex } = decision;
        if (!Number.isInteger(columnIndex) || columnIndex < 0 || columnIndex >= input.columnCount) {
            issues.push(issue("COLUMN_OUT_OF_RANGE", Number.isInteger(columnIndex) ? columnIndex : null));
            continue;
        }
        if (seen.has(columnIndex)) {
            issues.push(issue("COLUMN_DUPLICATE", columnIndex));
            continue;
        }
        seen.add(columnIndex);
        const targetField = decision.targetField ?? null;
        if (decision.decision === "MAPPED") {
            if (targetField === null) {
                issues.push(issue("DECISION_SHAPE", columnIndex));
                continue;
            }
            const target = findImportTargetField(targetField);
            if (target === undefined) {
                issues.push(issue("TARGET_UNKNOWN", columnIndex, targetField));
                continue;
            }
            if (!target.multiple) {
                const claims = (targetClaims.get(targetField) ?? 0) + 1;
                targetClaims.set(targetField, claims);
                if (claims > 1)
                    issues.push(issue("TARGET_DUPLICATE", columnIndex, targetField));
            }
            if (targetField === "name")
                nameMapped = true;
            if (targetField === "item_type")
                itemTypeMapped = true;
        }
        else {
            if (targetField !== null || decision.remember === true) {
                issues.push(issue("DECISION_SHAPE", columnIndex));
                continue;
            }
            if (decision.decision === "UNMAPPED" && input.stage === "submit") {
                issues.push(issue("COLUMN_UNDECIDED", columnIndex));
            }
        }
    }
    for (let columnIndex = 0; columnIndex < input.columnCount; columnIndex += 1) {
        if (!seen.has(columnIndex))
            issues.push(issue("COLUMN_MISSING", columnIndex));
    }
    if (itemTypeMapped && input.defaultItemType !== null) {
        issues.push(issue("ITEM_TYPE_CONFLICT", null, "item_type"));
    }
    if (input.stage === "submit") {
        if (!nameMapped)
            issues.push(issue("NAME_NOT_MAPPED", null, "name"));
        if (!itemTypeMapped && input.defaultItemType === null) {
            issues.push(issue("ITEM_TYPE_MISSING", null, "item_type"));
        }
    }
    return Object.freeze(issues);
}
/** The counts the stepper shows: "12 mapped, 2 ignored, 1 not mapped". */
export function countDecisions(decisions) {
    let mapped = 0;
    let ignored = 0;
    let unmapped = 0;
    for (const { decision } of decisions) {
        if (decision === "MAPPED")
            mapped += 1;
        else if (decision === "IGNORED")
            ignored += 1;
        else
            unmapped += 1;
    }
    return Object.freeze({ mapped, ignored, unmapped });
}
